import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Remove the static SEO baseline (index.html [data-default-seo]) once the app mounts,
// so JS clients/crawlers see only the accurate per-page metadata React injects (no duplicates).
function stripDefaultSeo() {
  if (typeof document === "undefined") return;
  document.querySelectorAll("[data-default-seo]").forEach((el) => el.remove());
}

export const SITE_URL = "https://elywellbeing.co.uk";
export const SITE_NAME = "Ely Wellbeing";
const DEFAULT_TITLE = "Ely Wellbeing | Know Thyself, Heal Thyself";
const DEFAULT_DESC =
  "Ely Wellbeing — Know Thyself, Heal Thyself. A training centre & therapy hub blending modern practice with the ancient Wu'sabat path.";

// React 19 natively hoists <title>/<meta>/<link> rendered anywhere into <head>.
// Centralised, non-misleading SEO: unique title/description/canonical + Open Graph/Twitter + optional JSON-LD.
export function Seo({ title, description, path, image, type = "website", jsonLd, noindex = false }) {
  const location = useLocation();
  useEffect(() => { stripDefaultSeo(); }, []);
  const canonicalPath = path || location.pathname || "/";
  const url = SITE_URL + canonicalPath;
  const img = image || `${SITE_URL}/ely-logo.webp`;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(b) }} />
      ))}
    </>
  );
}

export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/ely-logo.webp`,
  slogan: "Know Thyself, Heal Thyself",
};
